import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import { Box } from '@mui/system'
import { useResponsive } from 'hooks/useResponsive'
import { clamp } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import ltvDynamicColor from 'utils/ltvDynamicColor'
import { CoreProgressLabel } from './CoreProgressLabel'

const ProgressBarWrapper = styled(Box)`
  background: #f4fbe0;
  border: 1px solid rgba(255, 255, 255, 0.71);
  box-sizing: border-box;
  box-shadow: inset 0px 2px 4px rgba(140, 125, 116, 0.14);
  border-radius: 31px;
  width: 100%;
  height: 24px;
  display: flex;
  align-items: center;
  padding: 6px 4px;
  position: relative;
  margin-top: 40px;
  cursor: default;
`

const InternalBar = styled(Box)<{ percent: number }>`
  background: ${({ percent }) => ltvDynamicColor(percent)};
  box-shadow: 0px 0px 10px rgba(93, 188, 162, 0.24);
  border-radius: 8px;
  height: 16px;
  min-width: 16px;
  display: flex;
  justify-content: end;
  align-items: center;
`

const Label = styled(Typography)<{ flipped: number }>`
  font-weight: 600;
  transform: ${({ flipped }) => (flipped ? 'none' : 'translateX(110%)')};
  padding-right: 0.5;
  white-space: nowrap;
`

interface Props {
  ratio: number
  label?: string
  stepLabel?: { label: string; ratio: number }[]
}

const CoreProgressBar = ({ ratio: _ratio, label, stepLabel }: Props) => {
  const [flipped, setFlipped] = useState<boolean>(false)
  const { isMobile } = useResponsive()
  const ratio = useMemo(() => {
    const _r = isNaN(_ratio) ? 0 : _ratio
    return clamp(_r, 0, 100)
  }, [_ratio])

  useEffect(() => {
    if (isMobile) {
      if (ratio > 75) setFlipped(true)
      else setFlipped(false)
    } else {
      if (ratio > 85) setFlipped(true)
      else setFlipped(false)
    }
  }, [isMobile, ratio])

  return (
    <ProgressBarWrapper>
      <InternalBar width={`${ratio}%`} percent={ratio}>
        <Label
          variant="overline"
          color={flipped ? '#F4FBE0' : ltvDynamicColor(ratio)}
          flipped={flipped ? 1 : 0}
        >
          {label}
        </Label>
      </InternalBar>
      {stepLabel?.map((step) => (
        <CoreProgressLabel
          label={step.label}
          percent={step.ratio}
          key={step.label}
        />
      ))}
    </ProgressBarWrapper>
  )
}

export default CoreProgressBar
